import { useRef, useState } from "react";
import type { RefObject } from "react";
import { useIntl, type IntlShape } from "react-intl";
import { useMutation } from "@apollo/client/react";
import { CreateCompanyDocument, type CreateCompanyMutation } from "@/graphql/generated/operations";
import { userErrorMessage } from "@/lib/user-error";
import { companyValidationMessage } from "./company-admin-error";
import {
  companyDraftError,
  toCompanyProfileInput,
  type CompanyProfileDraft,
} from "./company-profile-draft";
import type { CompanyCreateGate, CompanyLogoInput } from "./use-company-admin";

// Bringing the company into existence — the create half of the ONE surface @adr SPARK-0038-creating-a-company-is-the-company-page-before-it-exists@2 describes.
//
// The company page and the registration form are the same screen; the only thing the create state
// lacks is an id. So this hook is not "the create page's controller": it is the gate every write on
// that screen goes through while the company is still hypothetical, and it disappears the moment
// genesis has answered.
//
// ONE REQUEST CARRIES THE WHOLE PROFILE, LOGO INCLUDED. `CreateCompanyDataInput.logo` is a real,
// implemented, integration-tested path in genesis (CompanyController#createCompany validates the
// bytes, uploads to backdrop and writes `logo_id` inside the same transaction as the company row and
// the creator's admin membership), so there is no window in which the company exists without the
// image the admin chose. Expertise, people and contact persons cannot travel with it — they point at
// a row that has to exist first — which is what the gate below is for.
//
// THE RESULT DELIBERATELY DOES NOT SELECT `logo`. genesis resolves Company.logo on the
// single-company query, `myCompanies` and `companyIdentities` and nowhere else; on a mutation
// payload it answers null, and Apollo would write that null over the real logo in the normalized
// cache. The page reads the stored logo back through CompanyProfile once the id exists.

export interface CompanyCreateSource {
  profile: CompanyProfileDraft;
  /** The picked, unwritten logo, or null. Travels inside the create rather than after it. */
  logo: CompanyLogoInput | null;
}

export interface CompanyCreation {
  /**
   * The gate handed to {@link useCompanyAdmin}. Latched on SUCCESS ONLY: every write on the page
   * asks for the same company, and the flush drivers fan out with `Promise.all`, so several of them
   * ask in the same tick. A REFUSAL clears the latch — a name the admin still has to fix must not
   * leave the page permanently unable to create anything.
   */
  ensure: CompanyCreateGate;
  busy: boolean;
  /** The last refusal, already localized (@adr SPARK-0010-no-raw-server-errors-in-ui@1). Cleared by the next attempt. */
  message: string | null;
}

/**
 * The one createCompany request, for the company page and for the Studio proposal card, which creates a
 * researched company the same way (and checks it first with `dryRun`).
 */
export function companyCreateVariables(source: CompanyCreateSource, dryRun: boolean) {
  const input = toCompanyProfileInput(source.profile);
  return {
    input: {
      data: {
        name: input.name,
        industry: input.industryId ? { id: input.industryId } : null,
        employeeCount: input.employeeCount,
        summary: input.summary,
        logo: source.logo,
      },
      dryRun,
    },
  };
}

type CreateCompanyOutput = CreateCompanyMutation["createCompany"];

/** What genesis's answer means: the new id, or a localized refusal (@adr SPARK-0010-no-raw-server-errors-in-ui@1). */
export function companyCreateOutcome(
  intl: IntlShape,
  result: CreateCompanyOutput | null | undefined,
): Awaited<ReturnType<CompanyCreateGate>> {
  if (result?.__typename === "CreateCompanyResult") return { ok: true, id: result.result.id };
  return {
    ok: false,
    message: companyValidationMessage(
      intl,
      result,
      intl.formatMessage({
        id: "company.create.failed",
        defaultMessage: "The company could not be created.",
      }),
    ),
  };
}

export function useCompanyCreation(
  source: RefObject<CompanyCreateSource>,
): CompanyCreation {
  const intl = useIntl();
  const [createCompany, state] = useMutation(CreateCompanyDocument);
  const [message, setMessage] = useState<string | null>(null);
  const inFlight = useRef<ReturnType<CompanyCreateGate> | null>(null);

  async function create(): ReturnType<CompanyCreateGate> {
    // The same validation the profile draft applies on /c/:id, applied before the round trip. A
    // company with no name is the one refusal the admin meets most, and genesis's answer to it is
    // an issue list rather than a sentence.
    const invalid = companyDraftError(intl, source.current.profile);
    if (invalid) {
      setMessage(invalid);
      return { ok: false, message: invalid };
    }
    try {
      const response = await createCompany({ variables: companyCreateVariables(source.current, false) });
      const outcome = companyCreateOutcome(intl, response.data?.createCompany);
      setMessage(outcome.ok ? null : outcome.message);
      return outcome;
    } catch (error) {
      const failed = userErrorMessage(intl, error);
      setMessage(failed);
      return { ok: false, message: failed };
    }
  }

  // Not memoised on purpose: useCompanyAdmin reads the gate from a ref at call time, so a fresh
  // identity per render costs nothing, and the latch lives in `inFlight`, which survives all of them.
  const ensure: CompanyCreateGate = () => {
    inFlight.current ??= create().then((result) => {
      if (!result.ok) inFlight.current = null;
      return result;
    });
    return inFlight.current;
  };

  return { ensure, busy: state.loading, message };
}
